import { getCookie } from 'utility/getCookie';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ssoLoginStart } from 'store/authentication/authenticationAction';
import * as auth from 'utility/Authentication';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { getSearchParam, safeUrlStartsWith } from 'hooks/useSearchParam';
import AuthenticationService from 'services/AuthenticationService';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import {
  isExternalUrl,
  loginSessionStorage,
  oauthSessionStorage,
  redirectLocalStorage,
} from 'storage/auth';

const KIZEN_SSO = 'saml_data';

const hasValidSSORedirect = (location) => {
  const success = getSearchParam(location, 'success');
  return success === 'true';
};

const hasValidRelayState = (location) => {
  const relaystate = getSearchParam(location, 'RelayState') || '';
  if (relaystate && relaystate.toLowerCase().startsWith('http')) {
    const origin = location.origin.toLowerCase();
    if (relaystate.toLowerCase().startsWith(origin)) {
      return relaystate.replace(new RegExp(`^${origin}`, 'i'), '');
    }

    // an absolute url on the api host (e.g. the oauth authorize endpoint) is
    // navigated to directly rather than routed through react-router
    return relaystate.toLowerCase().startsWith(safeUrlStartsWith)
      ? relaystate
      : null;
  }

  return relaystate && relaystate.startsWith('/') ? relaystate : null;
};

const SsoLogin = ({ title }) => {
  const { t } = useTranslation();
  useSetTitleOnLoad(title(t));
  const history = useHistory();
  const dispatch = useDispatch();
  useAsync(async () => {
    const validRedirect = hasValidSSORedirect(window.location);
    const cookie = getCookie(KIZEN_SSO + '');

    if (cookie === '' || !validRedirect) {
      // don't fail the logout sequence if the api call fails
      try {
        await AuthenticationService.logout();
      } catch (error) {}
      auth.status.clear();
      history.push('/login');
    }

    auth.status.authorized = true;

    const [error, { chosenBusiness } = {}] = await dispatch(
      ssoLoginStart(loginSessionStorage.val)
    );

    if (error) {
      history.replace('/login');
    } else {
      const hasRelayState = hasValidRelayState(window.location);

      if (isExternalUrl(hasRelayState)) {
        // external link (the api host) so react-router cannot handle it. let the
        // app finish booting and have the route wrappers navigate to it, otherwise
        // we leave the spa before the session is fully established
        redirectLocalStorage.path = '';
        oauthSessionStorage.val = hasRelayState;
        history.replace(chosenBusiness ? '/' : '/choose-business');
        return;
      }

      if (hasRelayState && !chosenBusiness) {
        // multiple businesses, save the redirect path for after business selection
        redirectLocalStorage.path = hasRelayState;
      } else {
        // we are loged in, with a valid business and have valid redirect
        redirectLocalStorage.path = '';
      }
      const hasBusinessRedirect = hasRelayState ? hasRelayState : '/';

      history.replace(
        chosenBusiness ? hasBusinessRedirect : '/choose-business'
      );
    }
  }, [history, dispatch]);
  return null;
};

export default SsoLogin;
